import * as React from "react"
import { HTMLAttributes, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import styled from "styled-components/macro"
import { ConversationType } from "../../API"
import branding from "../../branding/branding"
import { ChatConversationParam } from "../../communicationArea/ChatPage"
import { DetailNavLink } from "../../contentArea/detailPages/DetailNavLink"
import { useAppState } from "../../globalStates/AppState"
import { useLanguageState } from "../../globalStates/LanguageState"
import { useLoggedInState } from "../../globalStates/LoggedInUser"
import {
    IconCamera,
    IconCameraOff,
    IconChat,
    IconEndCall,
    IconLowerHand,
    IconMicrophone,
    IconMicrophoneOff,
    IconRaiseHand,
    IconSettings,
    IconShareScreen,
    IconShareScreenStop,
    IconVolumeHigh,
    IconVolumeOff
} from "../../ui/Icons"
import { accessPresenceState, EventType } from "../../ui/PresenceIndicator"
import { MeetingRoomGroupType, MeetingRoomType } from "../AudioVideoBranding"
import { useAudioContext } from "../context/AudioContext"
import { useChimeContext } from "../context/ChimeContext"
import { useContentShareContext } from "../context/ContentShareContext"
import { useModContext } from "../context/ModContext"
import { useRosterContext } from "../context/RosterContext"
import { useVideoContext } from "../context/VideoContext"
import { LocalVideoStatus } from "../enums/LocalVideoStatus"
import { MeetingStatusCode } from "../enums/MeetingStatusCode"
import { Button } from "./Button"
import LocalVideo from "./LocalVideo"
import RemainingTime from "./RemainingTime"
import { RoundTableEndCountdownTimer } from "./RoundTableCountDown"

/* #region  Style */

const ControlsRoot = styled.div`
    display: flex;
    flex-wrap: nowrap;
    height: 80px;
    width: 100%;
    z-index: 10;
    margin: 0 auto;
`

const HostedByColumn = styled.div`
    width: 180px;
    padding: 20px 30px;

    & :first-child {
        font-family: ${branding.font1};
        font-size: 0.9rem;
    }
    & :nth-child(2) {
        font-family: ${branding.font2};
        font-size: 1.3rem;
    }
`

export const ButtonsColumn = styled.div`
    flex-grow: 1;
    display: flex;
    flex-wrap: nowrap;
    margin: 0 auto;
    align-items: center;
    justify-content: center;
    border-top: 1px solid #fff;
    position: relative;

    & > button {
        position: relative;
        margin: 0;
        border-radius: 0;
        width: 60px;
        height: 40px;

        @media (max-width: 1500px) {
            width: 50px;
            height: 33px;
        }

        &:before {
            content: "";
            display: block;
            position: absolute;
            right: 0;
            height: 20px;
        }

        &:first-child {
            border-top-left-radius: 6px;
            border-bottom-left-radius: 6px;
        }

        &:last-child {
            border-top-right-radius: 6px;
            border-bottom-right-radius: 6px;

            &:before {
                display: none;
            }
        }

        background-color: buttonface;

        &:disabled {
            background-color: ${branding.communicationAreaDefaultAvatarColor} !important;
        }
    }
`

const LocalVideoColumn = styled.div<{ isRosterOpen?: boolean | null }>`
    justify-self: right;
    width: 145px;
    text-align: right;
    position: absolute;
    right: 0;

    ${({ isRosterOpen }) =>
        !isRosterOpen &&
        `
     @media(max-width: 1200px) {
      bottom: 65px;
  }
  `}

    ${({ isRosterOpen }) =>
        isRosterOpen &&
        `
      @media(max-width: 1400px) {
        bottom: 65px;
    }
    `}
`
/* #endregion */
interface DraggingProps {
    isDragging?: boolean
}

interface Props extends HTMLAttributes<HTMLDivElement>, EndCallButtonProps, OpenChatButtonProps {
    isPictureInPictureMode?: boolean
}

export default function Controls(props: Props) {
    const chime = useChimeContext()
    const modContext = useModContext()
    const audio = useAudioContext()
    const rosterContext = useRosterContext()
    const [hoster, setHoster] = useState<{
        organizationId: string | undefined
        organizationName: string | undefined
    } | null>(null)
    const strings = useLanguageState().getStrings()
    const profileId = useLoggedInState().user()?.profileId

    async function shutDown() {
        if (chime.getKind() === "call") window.sessionStorage.removeItem("currentTabCall")
        const meetingStatus = Object.assign({}, chime.getMeetingStatus())
        await chime.leaveRoom(meetingStatus)
        // reset everything when leaving the room
        audio.setMutedByMod(false)
        audio.realtimeUnmuteLocalAudio()
        audio.setVolume(100)
    }

    useEffect(
        () => {
            const meetingName = chime.getExternalMeetingId()

            let found = false
            for (const meetingRoomGroup of strings.meetingRoomGroups) {
                for (const meetingRoom of meetingRoomGroup.meetingRooms) {
                    if (meetingRoom.id === meetingName) {
                        setHoster(meetingRoomGroup)
                        found = true
                        break
                    }
                }
                if (found) break
            }

            if (chime.getKind() === "breakout" && !modContext.isMod()) {
                audio.realtimeMuteLocalAudio()
            } else if (chime.getKind() === "virtualCafe" && !modContext.isMod()) {
                const muteOnJoin: boolean | undefined = branding.meetingRoomGroups
                    .map((meetingRoomGroup: MeetingRoomGroupType) => meetingRoomGroup.meetingRooms)
                    .flat()
                    .find(
                        (meetingRoom: MeetingRoomType) => meetingRoom.id === chime.getExternalMeetingId()?.replace("vc_", "")
                    )?.muteNonModeratorsOnJoin

                if (muteOnJoin) {
                    audio.setMutedByMod(true)
                    audio.realtimeMuteLocalAudio()
                }
            }

            return () => {
                if (!chime.keepCallActive()) {
                    shutDown()
                }
            }
        },
        // eslint-disable-next-line
        []
    )

    if (profileId === undefined) {
        return <div>Not logged in</div>
    } else {
        return (
            <ControlsRoot className={props.className}>
                {!props.isPictureInPictureMode && hoster && hoster.organizationId && hoster.organizationName && (
                    <HostedByColumn>
                        <div>{strings.globalTexts.hostedByText}</div>
                        <DetailNavLink id={hoster.organizationId} name={hoster.organizationName} type="organization" source="VC">
                            {hoster.organizationName}
                        </DetailNavLink>
                    </HostedByColumn>
                )}
                <RemainingTime />
                {chime.getKind() === "roundtable" && <RoundTableEndCountdownTimer />}
                <RemainingTime forceShowTimer={chime.getKind() === "breakout" && chime.getTimeLeft()! > 0} />
                <ButtonsColumn>
                    <MuteButton {...props} />
                    <SoundOnOffButton {...props} />
                    <CamOnOffButton {...props} />
                    <ShareScreenButton {...props} />
                    <RaiseHandButton {...props} />
                    <OpenChatButton {...props} />
                    {!props.isPictureInPictureMode && <SettingsButton {...props} />}
                    <EndCallButton {...props} />
                </ButtonsColumn>
                {!props.isPictureInPictureMode && (
                    <LocalVideoColumn isRosterOpen={rosterContext.isOpen()}>
                        <LocalVideo />
                    </LocalVideoColumn>
                )}
            </ControlsRoot>
        )
    }
}

/* #region  MuteButton */
function MuteButton(props: DraggingProps) {
    const strings = useLanguageState().getStrings()
    const audioContext = useAudioContext()
    const muted = audioContext.isMuted()

    const onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (props.isDragging) {
            return
        }

        e.stopPropagation()
        if (muted) {
            audioContext.realtimeUnmuteLocalAudio()
        } else {
            audioContext.realtimeMuteLocalAudio()
        }
        // Adds a slight delay to close the tooltip before rendering the updated text in it
        await new Promise((resolve) => setTimeout(resolve, 10))
    }

    return (
        <Button
            tooltip={muted ? strings.conferenceTexts.unmute : strings.conferenceTexts.mute}
            icon={
                muted
                    ? IconMicrophoneOff({ fill: branding.sideIconBar.sideIconColorDark, width: "22", height: "22" })
                    : IconMicrophone({ fill: branding.sideIconBar.sideIconColorDark, width: "22", height: "22" })
            }
            onClick={onClick}
        />
    )
}
/* #endregion */

/* #region  SoundOnOffButton */
function SoundOnOffButton(props: DraggingProps) {
    const strings = useLanguageState().getStrings()
    const audioContext = useAudioContext()

    const onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (props.isDragging) {
            return
        }

        e.stopPropagation()
        audioContext.setVolume(audioContext.getVolume() === 0 ? 100 : 0)
    }

    return (
        <Button
            tooltip={audioContext.getVolume() === 100 ? strings.conferenceTexts.soundOff : strings.conferenceTexts.soundOn}
            icon={
                audioContext.getVolume() === 100
                    ? IconVolumeHigh({ fill: branding.sideIconBar.sideIconColorDark })
                    : IconVolumeOff({ fill: branding.sideIconBar.sideIconColorDark })
            }
            onClick={onClick}
        />
    )
}
/* #endregion */

/* #region  CamOnOffButton */
function CamOnOffButton(props: DraggingProps) {
    const strings = useLanguageState().getStrings()
    const videoContext = useVideoContext()
    const videoStatus = videoContext.getLocalVideoStatus()

    const onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (props.isDragging) {
            return
        }

        e.stopPropagation()
        videoContext.toggleLocalVideoTile()
    }

    return (
        <Button
            tooltip={
                videoStatus === LocalVideoStatus.Disabled ? strings.conferenceTexts.videoOn : strings.conferenceTexts.videoOff
            }
            icon={
                videoStatus === LocalVideoStatus.Enabled
                    ? IconCamera({ fill: branding.sideIconBar.sideIconColorDark })
                    : IconCameraOff({ fill: branding.sideIconBar.sideIconColorDark })
            }
            disabled={!videoContext.hasWebcam() || videoContext.getLocalVideoStatus() === LocalVideoStatus.Loading}
            onClick={onClick}
        />
    )
}

/* #region  ShareScreenButton */
function ShareScreenButton(props: DraggingProps) {
    const strings = useLanguageState().getStrings()
    const chime = useChimeContext()
    const contentShareContext = useContentShareContext()
    const modContext = useModContext()

    const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (props.isDragging) {
            return
        }

        e.stopPropagation()
        if (contentShareContext.isLocalAttendeeSharingScreen()) {
            contentShareContext.stopContentShare()
        } else {
            contentShareContext.startContentShareFromScreenCapture()
        }
    }

    return (
        <Button
            tooltip={
                contentShareContext.isLocalAttendeeSharingScreen()
                    ? strings.conferenceTexts.shareScreenStop
                    : strings.conferenceTexts.shareScreenStart
            }
            icon={
                contentShareContext.isLocalAttendeeSharingScreen()
                    ? IconShareScreenStop({ fill: branding.sideIconBar.sideIconColorDark })
                    : IconShareScreen({ fill: branding.sideIconBar.sideIconColorDark })
            }
            disabled={
                (!contentShareContext.isLocalAttendeeSharingScreen() && contentShareContext.isSharingScreen()) ||
                ((chime.getKind() === "breakout" || chime.getKind() === "showroom") && !modContext.isMod())
            }
            onClick={onClick}
        />
    )
}

/* #region  ShareLocalVideoButton
const FakeFileSelectWrapper = styled.div`
  position: relative;
  height: 50px;
  width: 50px;

  & button {
    position: absolute;
    pointer-events: none;
    top: 0;
    left: 0;
  }

  & input {
    display: block;
    width: 100%;
    height: 100%;
  }
`
// React/TypeScript does not yet expose captureStream
interface HTMLMediaElementWithCaputreStream extends HTMLVideoElement {
  captureStream(): MediaStream
  mozCaptureStream(): MediaStream
}
declare const InstallTrigger: any
function ShareLocalVideoButton(props: DraggingProps) {
  const strings = useLanguageState().getStrings()
  const videoContext = useVideoContext()
  const contentShareContext = useContentShareContext()
  const videoStatus = videoContext.getLocalVideoStatus()
  const videoRef = useRef<HTMLMediaElementWithCaputreStream | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.isDragging) {
      return
    }

    e.stopPropagation()
    videoContext.toggleLocalVideoTile()
  }

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const videoElement: HTMLMediaElementWithCaputreStream | null = videoRef.current
    if (!e.target.files || !videoElement || !inputRef.current)
      return
    const file = e.target.files[0]
    const type = file.type

    let canPlay = videoElement.canPlayType(type)

    if (canPlay === '') {
      // TODO Inform User
      console.log(`Cannot play type ${type}: ${(canPlay === '' ? 'no' : canPlay)}`)
      return
    }

    const fileURL = URL.createObjectURL(file)

    videoElement.src = fileURL
    await videoElement.play()
    let stream

    if (isFirefox) {
      stream = videoElement.mozCaptureStream()
    } else {
      stream = videoElement.captureStream()
    }
    contentShareContext.playVideoFile(stream)
    inputRef.current.value = ''
  }

  return <>
    <video style={{ "position": "absolute", "top": "-0", "height": "200px", "width": "200px" }} ref={videoRef} controls={true} />
    <FakeFileSelectWrapper style={{ "height": "50px", "width": "50px" }}>
      <input type="file" onChange={onChange} ref={inputRef} />
      <Button
        tooltip={videoStatus === LocalVideoStatus.Disabled ? strings.conferenceTexts.videoOn : strings.conferenceTexts.videoOff}
        icon={videoStatus === LocalVideoStatus.Enabled ? IconCamera({ fill: branding.sideIconBar.sideIconColorDark }) : IconCameraOff({ fill: branding.sideIconBar.sideIconColorDark })}
        disabled={!videoContext.hasWebcam()}
        onClick={onClick}
      />
    </FakeFileSelectWrapper>
  </>
}
 #endregion */

/* #endregion */

/* #region  RaiseHandButton */
function RaiseHandButton(props: DraggingProps) {
    const strings = useLanguageState().getStrings()
    const rosterContext = useRosterContext()

    const isHandRaised = rosterContext.isMyHandRaised()

    const onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (props.isDragging) {
            return
        }

        e.stopPropagation()
        rosterContext.raiseHand(rosterContext.getLocalAttendeeId()!, !isHandRaised)
    }
    return (
        <Button
            tooltip={isHandRaised ? strings.conferenceTexts.lowerHand : strings.conferenceTexts.raiseHand}
            icon={
                isHandRaised
                    ? IconLowerHand({ fill: branding.sideIconBar.sideIconColorDark, width: "23", height: "25" })
                    : IconRaiseHand({ fill: branding.sideIconBar.sideIconColorDark, width: "23", height: "25" })
            }
            onClick={onClick}
        />
    )
}

/* #endregion */

/* #region  OpenChatButton */
interface OpenChatButtonProps extends DraggingProps {
    isChatGroup?: boolean
}

function OpenChatButton(props: OpenChatButtonProps) {
    const strings = useLanguageState().getStrings()
    const chime = useChimeContext()
    const rosterContext = useRosterContext()
    const appState = useAppState()
    const profileId = useLoggedInState().user()?.profileId

    const onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (props.isDragging) {
            return
        }

        e.stopPropagation()

        const meetingKind = chime.getKind()
        const meetingId = chime.getExternalMeetingId()
        const conversationId = meetingId?.substr(3)
        switch (meetingKind) {
            case "call":
                const roster = rosterContext.getRoster()
                const rosterKeys = Object.keys(roster)
                const attendeeIds = rosterKeys.map((key) => roster[key].id)
                const opponentIds = attendeeIds.filter((id) => id !== profileId)
                if (!props.isChatGroup && opponentIds.length === 1 && opponentIds[0]) {
                    appState.setShowChatsTab(ChatConversationParam.privateConversationByOpponentId(opponentIds[0]))
                } else if (conversationId) {
                    appState.setShowChatsTab(
                        ChatConversationParam.conversationByConversationId(ConversationType.GROUP, conversationId)
                    )
                }
                break
            case "calenderEntry":
                if (conversationId) {
                    appState.setShowChatsTab(
                        ChatConversationParam.conversationByConversationId(ConversationType.CALENDARENTRY, conversationId)
                    )
                }
                break
            case "virtualCafe":
            case "showroom":
            case "breakout":
            case "conferenceroom":
            case "greenroom":
            case "roundtable":
                appState.setIsMeetingChatShown(true)
                rosterContext.setOpen(true)
                break
            default:
                // TODO behavior not yet defined
                break
        }
    }
    return (
        <Button
            tooltip={strings.conferenceTexts.openChat}
            icon={IconChat({ width: "20px", height: "20px", fill: branding.sideIconBar.sideIconColorDark })}
            onClick={onClick}
        />
    )
}
/* #endregion */

/* #region  SettingsButton */
function SettingsButton(props: DraggingProps) {
    const strings = useLanguageState().getStrings()
    const appState = useAppState()

    const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (props.isDragging) {
            return
        }

        e.stopPropagation()
        appState.setIsAudioVideoSettingsOpen(true)
    }

    return (
        <>
            <Button
                tooltip={strings.conferenceTexts.settings}
                icon={IconSettings({ fill: branding.sideIconBar.sideIconColorDark })}
                onClick={onClick}
            />
        </>
    )
}

/* #endregion */

/* #region  EndCallButton */
interface EndCallButtonProps extends DraggingProps {
    isConferenceRoom?: boolean
}
function EndCallButton(props: EndCallButtonProps) {
    const strings = useLanguageState().getStrings()
    const chime = useChimeContext()
    const history = useHistory()

    const onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (props.isDragging) {
            return
        }

        e.stopPropagation()
        chime.setKeepCallActive(false)
        chime.setMeetingStatus({ meetingStatusCode: MeetingStatusCode.Ended })

        // change presence only if user is not on masterclasses watching live stream
        if (history.location.pathname.indexOf("/masterclasses/") < 0) {
            accessPresenceState.updateMyPresence(EventType.EVENT_END)
        }

        if (props.isConferenceRoom) {
            if (window.localStorage.getItem("routeBeforeCall")) {
                history.push(window.localStorage.getItem("routeBeforeCall") as string)
            } else {
                history.push("/")
            }
        } else {
            // ConferenceOverlay
            chime.setShowConferenceOverlay(false)
        }
    }

    return (
        <Button
            tooltip={strings.conferenceTexts.leave}
            icon={IconEndCall({ fill: "#fff" })}
            color="#fff"
            backgroundColor="red"
            onClick={onClick}
        />
    )
}
/* #endregion */
